import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../../lib/utils";
import * as Icons from "lucide-react";

interface NavigationSection {
  title: string;
  slug: string;
  icon?: string;
  pages: Array<{
    title: string;
    slug: string;
  }>;
}

interface NavigationConfig {
  sections: NavigationSection[];
}

interface DocsSidebarProps {
  className?: string;
}

export function DocsSidebar({ className }: DocsSidebarProps) {
  const location = useLocation();
  const [navigation, setNavigation] = useState<NavigationConfig | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Load navigation.json
    fetch("/content/docs/navigation.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load navigation");
        return res.json();
      })
      .then((data: NavigationConfig) => {
        setNavigation(data);
        // Open sections that contain the current page
        const currentPath = location.pathname.replace("/docs/", "");
        const currentSection = data.sections.find((section) =>
          currentPath.startsWith(section.slug),
        );
        if (currentSection) {
          setOpenSections(new Set([currentSection.slug]));
        }
      })
      .catch((err) => {
        console.error("Failed to load navigation:", err);
        // Fallback to empty navigation
        setNavigation({ sections: [] });
      });
  }, [location.pathname]);

  const toggleSection = (slug: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const isActive = (sectionSlug: string, pageSlug: string) => {
    const currentPath = location.pathname.replace("/docs/", "");
    if (pageSlug === "index") {
      return currentPath === sectionSlug || currentPath === `${sectionSlug}/`;
    }
    return currentPath === `${sectionSlug}/${pageSlug}`;
  };

  if (!navigation) {
    return (
      <aside
        className={cn(
          "hidden md:block w-64 flex-shrink-0 border-r border-[var(--su-border-primary)] bg-[var(--su-bg-secondary)] self-stretch",
          className,
        )}
      >
        <div className="sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-[var(--su-bg-tertiary)] w-3/4 rounded-none" />
              <div className="h-4 bg-[var(--su-bg-tertiary)] w-full rounded-none" />
              <div className="h-4 bg-[var(--su-bg-tertiary)] w-5/6 rounded-none" />
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "hidden md:block w-64 flex-shrink-0 border-r border-[var(--su-border-primary)] bg-[var(--su-bg-secondary)] self-stretch",
        className,
      )}
    >
      <div className="sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
        <nav className="p-4">
          <Link
            to="/docs"
            className={cn(
              "block px-3 py-2 mb-4 font-semibold text-[var(--su-text-primary)] hover:text-[var(--su-purple-accent)] transition-colors",
              location.pathname === "/docs" && "text-[var(--su-purple-accent)]",
            )}
          >
            Documentation
          </Link>

          {navigation.sections.map((section) => {
            const IconName = section.icon as keyof typeof Icons;
            const IconComponent: React.ComponentType<{ className?: string }> =
              IconName && Icons[IconName]
                ? (Icons[IconName] as React.ComponentType<{
                    className?: string;
                  }>)
                : (Icons.Book as React.ComponentType<{ className?: string }>);
            const isOpen = openSections.has(section.slug);

            return (
              <div key={section.slug} className="mb-2">
                <button
                  onClick={() => toggleSection(section.slug)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-[var(--su-text-primary)] hover:bg-[var(--su-bg-tertiary)] transition-colors rounded-none"
                >
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-4 h-4" />
                    <span>{section.title}</span>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {isOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    {section.pages.map((page) => {
                      const pagePath =
                        page.slug === "index"
                          ? `/docs/${section.slug}`
                          : `/docs/${section.slug}/${page.slug}`;
                      const active = isActive(section.slug, page.slug);

                      return (
                        <Link
                          key={page.slug}
                          to={pagePath}
                          className={cn(
                            "block px-3 py-1.5 text-sm transition-colors rounded-none",
                            active
                              ? "text-[var(--su-purple-accent)] font-medium bg-[var(--su-bg-tertiary)]"
                              : "text-[var(--su-text-muted)] hover:text-[var(--su-text-primary)] hover:bg-[var(--su-bg-tertiary)]",
                          )}
                        >
                          {page.title}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
